/**
 * Active Sandbox run overrides. When inactive, getters return the provided fallback.
 */
import type { SandboxBaseMode, SandboxConfig } from "./sandbox-schema";

let active: SandboxConfig | null = null;
let runActive = false;

export function isSandboxRunActive(): boolean {
    return runActive;
}

export function getSandboxActiveConfig(): SandboxConfig | null {
    return active;
}

export function getSandboxBaseMode(): SandboxBaseMode | null {
    return active?.baseMode ?? null;
}

export function beginSandboxRun(cfg: SandboxConfig): void {
    active = cfg;
    runActive = true;
}

export function endSandboxRun(): void {
    active = null;
    runActive = false;
}

export function sbNum(modeKey: string, fallback: number): number {
    if (!runActive || !active) return fallback;
    const fromMode = active.mode[modeKey];
    if (typeof fromMode === "number" && Number.isFinite(fromMode)) return fromMode;
    const common = (active.common as Record<string, unknown>)[modeKey];
    if (typeof common === "number" && Number.isFinite(common)) return common;
    return fallback;
}

export function sbBool(modeKey: string, fallback: boolean): boolean {
    if (!runActive || !active) return fallback;
    const fromMode = active.mode[modeKey];
    if (typeof fromMode === "boolean") return fromMode;
    const common = (active.common as Record<string, unknown>)[modeKey];
    if (typeof common === "boolean") return common;
    return fallback;
}

export function sbCommonNum<K extends keyof SandboxConfig["common"]>(
    key: K,
    fallback: number,
): number {
    if (!runActive || !active) return fallback;
    const v = active.common[key];
    return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

export function sbCommonBool<K extends keyof SandboxConfig["common"]>(
    key: K,
    fallback: boolean,
): boolean {
    if (!runActive || !active) return fallback;
    const v = active.common[key];
    return typeof v === "boolean" ? v : fallback;
}

/** When a Sandbox run is active, rule toggles always come from config (never mode hardcodes). */
export function sbRuleEnabled(
    key: "ballCollisionsEnabled" | "wasdEnabled" | "progressionEnabled" | "magnetEnabled",
    fallback = false,
): boolean {
    if (!runActive || !active) return fallback;
    return !!active.common[key];
}

/** Bounce multiplier for modes that use 1.08 (or mode.bounceBoost). */
export function sbBounceBoost(fallback = 1.08): number {
    if (!runActive || !active) return fallback;
    const modeBoost = active.mode.bounceBoost;
    if (typeof modeBoost === "number" && modeBoost > 0) return modeBoost;
    const overlay = active.common.bounceSpeedBoost;
    if (typeof overlay === "number" && overlay > 0) return overlay;
    return fallback;
}
